import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import WorkCard from '../WorkCard/WorkCard'
import Reveal from '../Reveal/Reveal'
import SplitHeading from '../motion/SplitHeading'
import './WorkShowcase.scss'

/**
 * "Selected work" as a horizontal strip, after the case-study rail on
 * gojilabs.com: the intro sits on the left, the cards run off the right edge
 * of the screen all at one size, and the strip can be
 * dragged with the mouse, swiped on touch or stepped with the arrow buttons.
 * Cards slide in as they enter view (including when dragged into view), and
 * on hover the image zooms or the project clip plays (see WorkCard).
 *
 * Nothing depends on an animation to become visible: Reveal renders the final
 * state whenever entry motion is not allowed.
 */
const DRAG_THRESHOLD = 6
const pad = (n) => String(n).padStart(2, '0')

const WorkShowcase = ({ projects, onOpen, kicker, title, intro, allLabel, onAll, prevLabel, nextLabel }) => {
  const wrapRef = useRef(null)
  const stripRef = useRef(null)
  const drag = useRef({ down: false, moved: false, startX: 0, startScroll: 0, id: null })
  const [edges, setEdges] = useState({ start: true, end: false })
  const [current, setCurrent] = useState(0)

  // How far the strip may run past the content column to reach the screen edge.
  useLayoutEffect(() => {
    const setBleed = () => {
      const wrap = wrapRef.current
      if (!wrap) return
      const right = document.documentElement.clientWidth - wrap.getBoundingClientRect().right
      wrap.style.setProperty('--bleed', `${Math.max(0, Math.round(right))}px`)
    }
    setBleed()
    window.addEventListener('resize', setBleed)
    return () => window.removeEventListener('resize', setBleed)
  }, [])

  const updateEdges = useCallback(() => {
    const el = stripRef.current
    if (!el) return
    const max = el.scrollWidth - el.clientWidth
    setEdges({ start: el.scrollLeft <= 2, end: el.scrollLeft >= max - 2 })
    // The current project is the one nearest the strip's left edge; at the
    // very end the last one counts, even if it cannot reach the edge.
    const items = [...el.querySelectorAll('.work-strip-item')]
    if (!items.length) return
    if (el.scrollLeft >= max - 2) {
      setCurrent(items.length - 1)
      return
    }
    const base = items[0].offsetLeft
    let best = 0
    items.forEach((item, i) => {
      if (Math.abs(item.offsetLeft - base - el.scrollLeft) < Math.abs(items[best].offsetLeft - base - el.scrollLeft)) best = i
    })
    setCurrent(best)
  }, [])

  useEffect(() => {
    updateEdges()
    window.addEventListener('resize', updateEdges)
    return () => window.removeEventListener('resize', updateEdges)
  }, [updateEdges])

  // Mouse drag to scroll. Touch keeps the browser's native swipe.
  const onPointerDown = (e) => {
    if (e.pointerType !== 'mouse' || e.button !== 0) return
    drag.current = { down: true, moved: false, startX: e.clientX, startScroll: stripRef.current.scrollLeft, id: e.pointerId }
  }

  const onPointerMove = (e) => {
    const d = drag.current
    if (!d.down) return
    const dx = e.clientX - d.startX
    if (!d.moved && Math.abs(dx) > DRAG_THRESHOLD) {
      d.moved = true
      stripRef.current.classList.add('is-dragging')
      stripRef.current.setPointerCapture(d.id)
    }
    if (d.moved) {
      e.preventDefault()
      stripRef.current.scrollLeft = d.startScroll - dx
    }
  }

  const endDrag = () => {
    const d = drag.current
    if (!d.down) return
    d.down = false
    const el = stripRef.current
    el.classList.remove('is-dragging')
    if (d.id !== null && el.hasPointerCapture(d.id)) el.releasePointerCapture(d.id)
  }

  // A drag must not also count as a click on the card under the pointer.
  const onClickCapture = (e) => {
    if (drag.current.moved) {
      e.preventDefault()
      e.stopPropagation()
      drag.current.moved = false
    }
  }

  // Arrows move exactly one project, so the counter always lands on a number.
  const step = (dir) => {
    const el = stripRef.current
    if (!el) return
    const items = [...el.querySelectorAll('.work-strip-item')]
    const target = Math.min(Math.max(current + dir, 0), items.length - 1)
    el.scrollTo({ left: items[target].offsetLeft - items[0].offsetLeft, behavior: 'smooth' })
  }

  return (
    <section className='work-showcase' ref={wrapRef}>
      <div className='work-showcase-intro'>
        <Reveal>
          <p className='home-services-txt'>{kicker}</p>
          <SplitHeading text={title} className='home-released-projects home-work-title' />
          {intro && <p className='work-showcase-lead'>{intro}</p>}
        </Reveal>

        <Reveal delay={0.1} className='work-showcase-actions'>
          <button className='home-services-btn work-showcase-all' onClick={onAll}>
            {allLabel} <span aria-hidden='true'>↗</span>
          </button>
        </Reveal>
      </div>

      <div className='work-showcase-rail'>
        <div
          className='work-strip'
          ref={stripRef}
          onScroll={updateEdges}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
          onLostPointerCapture={endDrag}
          onClickCapture={onClickCapture}
          onDragStart={(e) => e.preventDefault()}
        >
          {projects.map((project, index) => (
            <Reveal
              key={project.title}
              direction='left'
              delay={Math.min(index, 3) * 0.08}
              className='work-strip-item'
            >
              <WorkCard project={project} onOpen={onOpen} />
            </Reveal>
          ))}
        </div>
        <div className='work-controls'>
          <button type='button' className='work-arrow' onClick={() => step(-1)} disabled={edges.start} aria-label={prevLabel}>
            <span aria-hidden='true'>←</span>
          </button>
          <button type='button' className='work-arrow' onClick={() => step(1)} disabled={edges.end} aria-label={nextLabel}>
            <span aria-hidden='true'>→</span>
          </button>
          <p className='work-count' aria-live='polite'>
            <span className='work-count-current'>{pad(current + 1)}</span> / {pad(projects.length)}
          </p>
          <div className='work-progress' aria-hidden='true'>
            <span style={{ transform: `scaleX(${(current + 1) / projects.length})` }} />
          </div>
        </div>
      </div>
    </section>
  )
}

export default WorkShowcase
